import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { catchError, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';

interface ApiResponse<T = unknown> {
  isSuccess: boolean;
  statusCode: number;
  result: T | null;
  errors: unknown[];
  version: string;
  environment: string;
}

export const ApiToastInterceptor: HttpInterceptorFn = (req, next) => {
  const messageService = inject(MessageService);
  const translate = inject(TranslateService);

  const showErrorToast = (errors?: unknown[]) => {
    const defaultSummary = translate.instant('common.error') || 'Error';
    const defaultDetail = translate.instant('common.something_went_wrong') || 'Something went wrong';
    let detail = defaultDetail;

    if (Array.isArray(errors) && errors.length > 0) {
      const messages = errors
        .map((err) => {
          const errObj = err as Record<string, unknown>;
          let raw: string = (errObj?.['message'] ?? errObj?.['Message'] ?? (typeof err === 'string' ? err : '')) as string;
          if (!raw && err != null) {
            try {
              raw = String(err);
            } catch {
              raw = '';
            }
          }
          if (!raw) return null;
          const colonIndex = raw.indexOf(':');
          const msg = colonIndex >= 0 ? raw.slice(colonIndex + 1) : raw;
          return msg.trim();
        })
        .filter((m) => !!m);

      if (messages.length > 0) {
        detail = messages.join(', ');
      }
    }

    messageService.add({ severity: 'error', summary: defaultSummary, detail });
  };

  return next(req).pipe(
    tap((event) => {
      if (!(event instanceof HttpResponse)) return;

      // JSON responses
      if (event.body && typeof event.body === 'object' && !(event.body instanceof Blob)) {
        const body = event.body as Record<string, unknown>;
        if (Object.prototype.hasOwnProperty.call(body, 'isSuccess')) {
          const apiBody = body as unknown as ApiResponse;
          // Skip auth-related statuses
          if (apiBody.statusCode === 401 || apiBody.statusCode === 403) return;
          if (apiBody.isSuccess === false) {
            showErrorToast(apiBody.errors);
          }
        }
        return;
      }

      // Blob responses that may contain JSON error
      if (event.body instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const text = reader.result as string;
            const parsed = JSON.parse(text) as Record<string, unknown>;
            if (parsed && typeof parsed === 'object' && 'isSuccess' in parsed) {
              const parsedResponse = parsed as unknown as ApiResponse;
              if (parsedResponse.statusCode === 401 || parsedResponse.statusCode === 403) return;
              if (parsedResponse.isSuccess === false) {
                showErrorToast(parsedResponse.errors);
              }
            }
          } catch {
            // Not JSON; ignore
          }
        };
        reader.readAsText(event.body);
      }
    }),
    catchError((error) => {
      // Show generic toast for HTTP errors except 401/403
      if (error instanceof HttpErrorResponse) {
        if (error.status !== 401 && error.status !== 403) {
          const errorsArr = Array.isArray(error.error?.errors) ? (error.error.errors as unknown[]) : undefined;
          if (errorsArr && errorsArr.length > 0) {
            showErrorToast(errorsArr);
          } else {
            const summary = translate.instant('common.error') || 'Error';
            const fallback = translate.instant('common.something_went_wrong') || 'Something went wrong';
            const raw = (error.error?.message ?? error.message ?? fallback) as string;
            const colonIndex = typeof raw === 'string' ? raw.indexOf(':') : -1;
            const parsed =
              typeof raw === 'string' ? (colonIndex >= 0 ? raw.slice(colonIndex + 1).trim() : raw) : fallback;
            messageService.add({ severity: 'error', summary, detail: parsed });
          }
        }
      } else {
        const summary = translate.instant('common.error') || 'Error';
        const detail = translate.instant('common.something_went_wrong') || 'Something went wrong';
        messageService.add({ severity: 'error', summary, detail });
      }
      return throwError(() => error);
    })
  );
};

